import { useUIStore } from '@/stores/uiStore'
import { getCategoryColorStyle } from '@/lib/categoryColors'
import { useTaskStore } from '@/stores/taskStore'
import { useCategoryStore } from '@/stores/categoryStore'
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react'
import { clsx } from 'clsx'
import {
  BarChart3,
  CheckSquare,
  ChevronDown,
  Grid2X2,
  HelpCircle,
  MessageCircle,
  Palette,
  Plus,
  Settings,
  Star,
  X,
  FileText,
} from 'lucide-react'
import { Fragment, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

export function MobileNav() {
  const navigate = useNavigate()

  const isMobileMenuOpen = useUIStore((state) => state.isMobileMenuOpen)
  const closeMobileMenu = useUIStore((state) => state.closeMobileMenu)
  const openSettingsModal = useUIStore((state) => state.openSettingsModal)
  const openFAQModal = useUIStore((state) => state.openFAQModal)
  const openTermsModal = useUIStore((state) => state.openTermsModal)
  const openTaskCreateModal = useUIStore((state) => state.openTaskCreateModal)
  const setCurrentView = useUIStore((state) => state.setCurrentView)

  const tasks = useTaskStore((state) => state.tasks)
  const categories = useCategoryStore((state) => state.categories)

  const [isCategoryExpanded, setIsCategoryExpanded] = useState(false)

  const starredTasks = tasks.filter((t) => t.isStarred && t.status === 'pending')

  const handleOpenSettings = () => {
    closeMobileMenu()
    openSettingsModal()
  }

  const handleOpenFAQ = () => {
    closeMobileMenu()
    openFAQModal()
  }

  const handleOpenTerms = () => {
    closeMobileMenu()
    openTermsModal()
  }

  const handleAddTask = () => {
    closeMobileMenu()
    openTaskCreateModal()
  }

  const handleStarredClick = () => {
    closeMobileMenu()
    setCurrentView('tasks')
    navigate('/?filter=starred')
  }

  // 하단 탭에 없는 목적지 — 분석(프로필)과 채팅 허브는 햄버거 메뉴에서 진입
  const handleAnalyticsClick = () => {
    closeMobileMenu()
    setCurrentView('profile')
    navigate('/profile')
  }

  const handleChatClick = () => {
    closeMobileMenu()
    setCurrentView('groups') // /chat은 그룹 영역으로 하이라이트 (채팅 탭 통합 정책)
    navigate('/chat')
  }

  const handleCategoryClick = (categoryId: number) => {
    closeMobileMenu()
    setCurrentView('tasks')
    navigate(`/?category=${categoryId}`)
  }

  const handleThemeClick = () => {
    closeMobileMenu()
    openSettingsModal()
  }

  // Body scroll lock when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden'
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isMobileMenuOpen])

  return (
    <Transition show={isMobileMenuOpen} as={Fragment}>
      <Dialog onClose={closeMobileMenu} className="relative z-50 lg:hidden" id="mobile-nav">
        {/* Backdrop */}
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-zinc-900/50 backdrop-blur-sm" aria-hidden="true" />
        </TransitionChild>

        {/* Drawer */}
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="-translate-x-full"
          enterTo="translate-x-0"
          leave="ease-in duration-200"
          leaveFrom="translate-x-0"
          leaveTo="-translate-x-full"
        >
          <DialogPanel
            className="workspace-sidebar fixed inset-y-0 left-0 flex h-[100dvh] w-[min(86vw,360px)] flex-col border-r border-[var(--workspace-border)] pb-safe shadow-e3"
            aria-labelledby="mobile-nav-title"
          >
            {/* Header */}
            <div
              className="flex min-h-16 items-center justify-between px-4 border-b border-[var(--workspace-border)] flex-shrink-0"
              style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
            >
              <div className="flex items-center gap-3">
                <div className="workspace-brand-mark w-10 h-10 rounded-[14px] flex items-center justify-center flex-shrink-0">
                  <CheckSquare className="w-5 h-5 text-white" aria-hidden="true" />
                </div>
                <div className="flex flex-col">
                  <span
                    id="mobile-nav-title"
                    className="font-bold text-heading"
                  >
                    Moonwave
                  </span>
                  <span className="text-meta text-caption">나만의 집중 공간</span>
                </div>
              </div>
              <button
                onClick={closeMobileMenu}
                className="p-2 -mr-2 rounded-lg hover-surface transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 min-w-[44px] min-h-[44px] flex items-center justify-center"
                aria-label="메뉴 닫기"
              >
                <X className="w-5 h-5 text-body" aria-hidden="true" />
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto py-4">
              {/* Add Task Button */}
              <div className="px-2 mb-4">
                <button
                  onClick={handleAddTask}
                  className="workspace-primary-action w-full flex min-h-[48px] items-center justify-center gap-2 rounded-xl px-4 text-sm font-semibold text-white transition-all active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                  aria-label="새 작업 추가"
                >
                  <Plus className="w-5 h-5" aria-hidden="true" />
                  <span className="font-medium">새 작업 추가</span>
                </button>
              </div>

              {/* Menu Items */}
              <nav className="px-2" aria-label="모바일 메인 메뉴">
                <ul className="space-y-1" role="menu">
                  {/* 분석 — 하단 탭에서 제외된 목적지 */}
                  <li role="none">
                    <button
                      onClick={handleAnalyticsClick}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-body hover-surface focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 min-h-[44px]"
                      role="menuitem"
                    >
                      <BarChart3 className="w-5 h-5" aria-hidden="true" />
                      <span className="font-medium">분석</span>
                    </button>
                  </li>

                  {/* 채팅 허브 — 하단 탭 통합 후 진입점 */}
                  <li role="none">
                    <button
                      onClick={handleChatClick}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-body hover-surface focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 min-h-[44px]"
                      role="menuitem"
                    >
                      <MessageCircle className="w-5 h-5" aria-hidden="true" />
                      <span className="font-medium">채팅</span>
                    </button>
                  </li>

                  {/* Starred Tasks */}
                  <li role="none">
                    <button
                      onClick={handleStarredClick}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-body hover-surface focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 min-h-[44px]"
                      role="menuitem"
                    >
                      <Star className="w-5 h-5" aria-hidden="true" />
                      <span className="font-medium">별표 작업</span>
                      {starredTasks.length > 0 && (
                        <span className="ml-auto text-xs text-caption bg-surface-tertiary px-2 py-0.5 rounded-full">
                          {starredTasks.length}
                        </span>
                      )}
                    </button>
                  </li>

                  {/* Categories - Expandable */}
                  <li role="none">
                    <button
                      onClick={() => setIsCategoryExpanded(!isCategoryExpanded)}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-body hover-surface focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 min-h-[44px]"
                      role="menuitem"
                      aria-expanded={isCategoryExpanded}
                    >
                      <Grid2X2 className="w-5 h-5" aria-hidden="true" />
                      <span className="font-medium">카테고리</span>
                      <ChevronDown
                        className={clsx(
                          'w-4 h-4 ml-auto transition-transform duration-200',
                          isCategoryExpanded && 'rotate-180'
                        )}
                        aria-hidden="true"
                      />
                    </button>
                    {isCategoryExpanded && categories.length > 0 && (
                      <ul className="mt-1 ml-6 space-y-0.5" role="menu">
                        {categories.map((category) => (
                          <li key={category.id} role="none">
                            <button
                              onClick={() => handleCategoryClick(category.id!)}
                              className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors text-body hover-surface focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 min-h-[44px] text-left"
                              role="menuitem"
                            >
                              <div
                                className="w-3 h-3 rounded-full flex-shrink-0"
                                style={{ backgroundColor: getCategoryColorStyle(category.color) }}
                                aria-hidden="true"
                              />
                              <span className="text-sm truncate">{category.name}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>

                  {/* Theme */}
                  <li role="none">
                    <button
                      onClick={handleThemeClick}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-body hover-surface focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 min-h-[44px]"
                      role="menuitem"
                    >
                      <Palette className="w-5 h-5" aria-hidden="true" />
                      <span className="font-medium">테마</span>
                    </button>
                  </li>

                  {/* FAQ */}
                  <li role="none">
                    <button
                      onClick={handleOpenFAQ}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-body hover-surface focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 min-h-[44px]"
                      role="menuitem"
                    >
                      <HelpCircle className="w-5 h-5" aria-hidden="true" />
                      <span className="font-medium">FAQ</span>
                    </button>
                  </li>

                  {/* Terms of Service */}
                  <li role="none">
                    <button
                      onClick={handleOpenTerms}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-body hover-surface focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 min-h-[44px]"
                      role="menuitem"
                    >
                      <FileText className="w-5 h-5" aria-hidden="true" />
                      <span className="font-medium">서비스 약관</span>
                    </button>
                  </li>

                  {/* Settings */}
                  <li role="none">
                    <button
                      onClick={handleOpenSettings}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-body hover-surface focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 min-h-[44px]"
                      role="menuitem"
                    >
                      <Settings className="w-5 h-5" aria-hidden="true" />
                      <span className="font-medium">설정</span>
                    </button>
                  </li>
                </ul>
              </nav>

              {/* Empty State */}
              {tasks.length === 0 && (
                <div className="px-6 py-8 text-center">
                  <p className="text-sm text-caption">
                    등록된 작업이 없습니다.
                  </p>
                  <p className="text-xs text-caption mt-1">
                    작업을 추가하여 시작하세요.
                  </p>
                </div>
              )}
            </div>
          </DialogPanel>
        </TransitionChild>
      </Dialog>
    </Transition>
  )
}
